/**
 * React Query bindings. One hook per read, one mutation factory per write.
 *
 * Polling intervals are deliberate: there is no WebSocket, so the browser
 * refreshes on a 15s cadence for money numbers and for order state, and every
 * screen that shows a polled number also shows how old it is.
 *
 * 15초를 고른 이유: `/holdings`(ASSET)·`/buying-power`(ORDER_INFO)는 각각 5·6 TPS 라
 * 폴링 3콜/15초는 예산에서 무시할 수 있고, 1 TPS 인 `ACCOUNT` 그룹(`/accounts`)은
 * 애초에 폴링하지 않습니다.
 */

import { useAuth } from "@/contexts/AuthContext";
import {
  accountsApi,
  adminApi,
  authApi,
  backtestsApi,
  bookApi,
  killSwitchApi,
  marketApi,
  notificationsApi,
  ordersApi,
  positionsApi,
  strategiesApi,
  tossApi,
} from "@/lib/api/endpoints";
import { resolveTimeZone } from "@/lib/datetime";
import { buildDslCatalog } from "@/lib/dsl-catalog";
import type {
  Account,
  AccountPatchBody,
  BacktestRequest,
  Currency,
  MarketCountry,
  NotificationSettings,
  Order,
  OrderFilter,
  RankingDuration,
  RankingType,
  RuleDsl,
} from "@/types/api";
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const ACCOUNT_POLL_MS = 15_000;
export const ORDER_POLL_MS = 15_000;

type Options<T> = Omit<UseQueryOptions<T, unknown, T, string[]>, "queryKey" | "queryFn">;

// ------------------------------------------------------------------- identity

export function useMe(options?: Options<Awaited<ReturnType<typeof authApi.me>>>) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["me"],
    queryFn: authApi.me,
    enabled: isAuthenticated,
    staleTime: 60_000,
    ...options,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: authApi.updateMe,
    onSuccess: (me) => qc.setQueryData(["me"], me),
  });
}

/**
 * 화면 시각을 그릴 타임존. `me` 가 아직 없거나 옛 서버라 필드가 없으면 기본값(서울)이에요 —
 * 로딩 동안 UTC 로 그렸다가 서울로 바뀌면 같은 시각이 9시간 튀어 보입니다.
 */
export function useTimeZone(): string {
  const me = useMe();
  return resolveTimeZone(me.data?.timezone);
}

// -------------------------------------------------------------- toss / account

export function useOnboardingGuide() {
  return useQuery({
    queryKey: ["toss", "onboarding"],
    queryFn: tossApi.onboarding,
    staleTime: 5 * 60_000,
  });
}

export function useCredentials() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["toss", "credentials"],
    queryFn: tossApi.credentials,
    enabled: isAuthenticated,
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tossApi.createCredential,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["toss", "credentials"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/**
 * 온보딩 "연결 확인" 화면용. 진단은 읽기 전용 프로브라 쿼리로 다룹니다 —
 * `verify` 는 저장된 상태를 갱신하는 별개 액션이고, 단계별 결과를 주지 않습니다.
 */
export function useCredentialDiagnosis(credentialId: string | null) {
  return useQuery({
    queryKey: ["toss", "credentials", credentialId, "diagnose"],
    queryFn: () => tossApi.diagnoseCredential(credentialId as string),
    enabled: credentialId !== null,
    staleTime: 0,
    retry: false,
  });
}

export function useVerifyCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tossApi.verifyCredential,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["toss", "credentials"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tossApi.deleteCredential,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["toss", "credentials"] }),
  });
}

export function useAccounts() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["accounts"],
    queryFn: accountsApi.list,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

/** The single account the MVP operates on (multi-account is P1). */
export function usePrimaryAccount(): {
  account: Account | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useAccounts();
  return { account: data?.[0] ?? null, isLoading };
}

export function useHoldings(accountId: string | null) {
  return useQuery({
    queryKey: ["holdings", accountId ?? "none"],
    queryFn: () => accountsApi.holdings(accountId as string),
    enabled: accountId !== null,
    refetchInterval: ACCOUNT_POLL_MS,
  });
}

/**
 * 매수가능금액. `currency` 마다 별도 호출이에요 — 토스가 통화별로만 답합니다.
 *
 * 계좌에 달러 예수금이 있는데 KRW 만 물으면 그 금액이 화면에서 사라져요(운영자 계좌에
 * 실제로 $6,570 이 있었습니다). 총 자산을 그리는 화면은 두 통화를 모두 불러야 합니다.
 */
export function useBuyingPower(accountId: string | null, currency: "KRW" | "USD" = "KRW") {
  return useQuery({
    queryKey: ["buying-power", accountId ?? "none", currency],
    queryFn: () => accountsApi.buyingPower(accountId as string, currency),
    enabled: accountId !== null,
    refetchInterval: ACCOUNT_POLL_MS,
  });
}

export function usePerformance(accountId: string | null, days: number, currency: Currency = "KRW") {
  return useQuery({
    // 통화가 키의 일부다 — 서버는 한 통화만 세고(원 + 달러를 더한 실현손익은 어떤 단위로도
    // 읽을 수 없다), 키에서 빼면 원화 성과를 캐시에 담아 둔 채 달러 화면을 그린다.
    queryKey: ["performance", accountId ?? "none", days, currency],
    queryFn: () => accountsApi.performance(accountId as string, days, currency),
    enabled: accountId !== null,
  });
}

/**
 * 일별 계좌 시계열. 매일 16:00 KST 에 한 번 갱신되는 데이터라 폴링하지 않아요 —
 * refetchInterval 을 붙이면 갱신처럼 보일 뿐 같은 값을 다시 받습니다.
 */
export function useAccountHistory(accountId: string | null, days: number) {
  return useQuery({
    queryKey: ["account-history", accountId ?? "none", String(days)],
    queryFn: () => accountsApi.history(accountId as string, days),
    enabled: accountId !== null,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AccountPatchBody & { id: string }) =>
      accountsApi.update(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["accounts"] }),
  });
}

export function useKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => killSwitchApi.engage(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["strategies"] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["positions"] });
      void qc.invalidateQueries({ queryKey: ["conditional-orders"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

// ------------------------------------------------------------------------ book

/**
 * 장부 요약 + 대조 결과. 상시 폴링하지 않는다 — Mongo 만 읽는 화면이라 `staleTime` 과
 * 수동 새로고침이면 충분하다.
 *
 * `readyToRead`(기본 true)로 호출 순서를 강제할 수 있다: 캐시 미스는 예외가 아니라
 * 기본값이라, `useBuyingPower` 를 먼저(또는 함께) 부르지 않으면 서버가 `cash_known:
 * false` 로 답한다. `BookPage` 는 매수가능금액 조회가 끝난 뒤에만 이 훅을 켠다.
 */
export function useBook(accountId: string | null, readyToRead = true, currency: Currency = "KRW") {
  return useQuery({
    // 통화가 키의 일부다 — 서버는 **통화 하나**만 보고(섞은 합계는 어떤 계좌 실측과도
    // 비교할 수 없다), 키에서 빼면 원화 장부를 캐시에 담아 둔 채 달러 화면을 그린다.
    queryKey: ["book", accountId ?? "none", currency],
    queryFn: () => bookApi.get(accountId as string, currency),
    enabled: accountId !== null && readyToRead,
    staleTime: 20_000,
  });
}

export function useBookEntries(
  accountId: string | null,
  params: { strategy_id?: string; symbol?: string; cursor?: string }
) {
  return useQuery({
    queryKey: [
      "book-entries",
      accountId ?? "none",
      params.strategy_id ?? "all",
      params.symbol ?? "all",
      params.cursor ?? "first",
    ],
    queryFn: () => bookApi.entries(accountId as string, { ...params, limit: 50 }),
    enabled: accountId !== null,
    staleTime: 20_000,
  });
}

export function useFundBook(accountId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: string; strategy_id?: string | null; currency?: Currency }) =>
      bookApi.fund(accountId as string, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["book", accountId ?? "none"] });
      void qc.invalidateQueries({ queryKey: ["book-entries", accountId ?? "none"] });
    },
  });
}

// ------------------------------------------------------------------ strategies

export function useStrategies() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["strategies"],
    queryFn: strategiesApi.list,
    enabled: isAuthenticated,
  });
}

export function useStrategy(id: string | undefined) {
  return useQuery({
    queryKey: ["strategies", id ?? "none"],
    queryFn: () => strategiesApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useDslSchema() {
  return useQuery({
    queryKey: ["dsl-schema"],
    queryFn: strategiesApi.schema,
    // 서버는 available 지표 목록만 준다 — 라벨·파라미터 메타데이터와의 교집합은 클라이언트가 만든다.
    select: buildDslCatalog,
    staleTime: 30 * 60_000,
  });
}

export function useStrategyRuns(id: string | undefined) {
  return useQuery({
    queryKey: ["strategy-runs", id ?? "none"],
    queryFn: () => strategiesApi.runs(id as string),
    enabled: Boolean(id),
    refetchInterval: ORDER_POLL_MS,
  });
}

/** 판단 로그 상세(지표 스냅샷 포함) — 목록에는 스냅샷이 없어서 펼칠 때만 가져온다. */
export function useStrategyRun(strategyId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["strategy-runs", strategyId, runId],
    queryFn: () => strategiesApi.run(strategyId, runId),
    enabled,
    staleTime: 60_000,
  });
}

export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: strategiesApi.create,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["strategies"] }),
  });
}

export function useUpdateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name: string;
      description?: string | null;
      rule: RuleDsl;
      symbol_exclusive?: boolean;
    }) =>
      strategiesApi.update(input.id, {
        name: input.name,
        description: input.description,
        rule: input.rule,
        symbol_exclusive: input.symbol_exclusive,
      }),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ["strategies"] });
      void qc.invalidateQueries({ queryKey: ["strategies", input.id] });
    },
  });
}

/** 프리셋 목록. **규칙 본문은 서버가 소유해요** — web 은 문구만 붙입니다. */
/**
 * 장 상태. 전역 헤더가 쓰므로 모든 화면에서 살아 있어요.
 *
 * 서버가 세션 문서를 캐시하고 있어서 상류(토스) 호출이 늘지 않아요 — `MARKET_INFO` 는
 * 3 TPS 뿐이라 화면이 직접 폴링하면 예산을 먹습니다. 1분 주기면 남은 시간 표시에 충분해요.
 */
export function useMarketCalendar(country: "KR" | "US" = "KR") {
  return useQuery({
    queryKey: ["market-calendar", country],
    queryFn: () => marketApi.calendar(country),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** 사전을 아직 못 받았을 때의 빈 사전. 매 렌더 새 객체를 만들면 소비자가 매번 다시 그려요. */
const NO_STOCK_NAMES: Record<string, string> = {};

/**
 * 심볼 → 종목명 사전. 표·뱃지 어디서 불러도 **쿼리 키가 하나**라 요청은 한 번이에요.
 *
 * 주문·포지션·원장·백테스트 응답에는 종목명이 없습니다(심볼만). 화면마다 심볼 목록을
 * 모아 서버에 되묻는 대신 사전을 통째로 캐시합니다 — 서버는 Mongo 만 읽고 토스를 부르지
 * 않으니 폴링 예산과 무관하고, 이름은 거의 변하지 않아요.
 */
export function useStockNames(): Record<string, string> {
  const query = useQuery({
    queryKey: ["stock-names"],
    queryFn: () => marketApi.stockNames(),
    staleTime: 60 * 60_000,
  });
  return query.data ?? NO_STOCK_NAMES;
}

/**
 * 종목 검색. 사용자가 입력할 때만 — 상시 폴링하지 않아요.
 *
 * `marketCountry` 를 주면 그 시장으로 좁혀요. 규칙 편집기는 항상 자기 규칙의 시장을
 * 넘깁니다 — 다른 시장 종목은 담아도 저장이 `SYMBOL_INVALID` 로 거부되니까요.
 */
export function useStockSearch(q: string, marketCountry?: MarketCountry) {
  const query = q.trim();
  return useQuery({
    queryKey: ["stock-search", query, marketCountry ?? "all"],
    queryFn: () => marketApi.searchStocks(query, 20, marketCountry),
    enabled: query.length > 0,
    staleTime: 60_000,
  });
}

/**
 * 종목 점검에 필요한 네 조회를 한 번에. 패널을 열 때만 돌아요.
 *
 * `warnings` 는 실패를 숨기지 않습니다 — 조회가 안 되면 진입을 막는 게 서버 규약이라
 * 화면도 "확인 못 했다" 를 그대로 보여줘야 해요.
 */
export function useStockInspection(symbol: string | null) {
  const enabled = symbol !== null && symbol !== "";
  const detail = useQuery({
    queryKey: ["stock", symbol ?? "none"],
    queryFn: () => marketApi.stock(symbol as string),
    enabled,
  });
  const warnings = useQuery({
    queryKey: ["stock-warnings", symbol ?? "none"],
    queryFn: () => marketApi.warnings(symbol as string),
    enabled,
  });
  const priceLimits = useQuery({
    queryKey: ["price-limits", symbol ?? "none"],
    queryFn: () => marketApi.priceLimits(symbol as string),
    enabled,
  });
  const orderbook = useQuery({
    queryKey: ["orderbook", symbol ?? "none"],
    queryFn: () => marketApi.orderbook(symbol as string),
    enabled,
    // 자동 갱신하지 않습니다. `/orderbook` 은 단일 심볼이고 TTL 이 1초라 캐시 흡수가 0인데,
    // 평가 경로가 후보 심볼마다 같은 버킷(MARKET_DATA)을 쓰기 때문에 패널이 상시 폴링하면
    // 진입 판정과 서로를 429 로 밀어냅니다. 패널을 열 때 한 번만 봅니다.
    refetchInterval: false,
  });
  return { detail, warnings, priceLimits, orderbook };
}

/**
 * 일봉 차트. 파라미터가 바뀔 때만 불러요 — **자동 갱신하지 않습니다.**
 *
 * `useStockInspection` 의 `orderbook` 과 같은 이유예요: `MARKET_DATA_CHART` 는 5 TPS 인데
 * 1분봉 스윕이 이미 3.33 TPS 를 쓰고 있어서, 화면이 상시 폴링하면 규칙 평가와 서로를
 * 429 로 밀어냅니다.
 */
export function useStockChart(
  symbol: string | null,
  params: { count?: number; overlays?: string[] } = {}
) {
  const count = params.count ?? 60;
  const overlays = params.overlays ?? [];
  return useQuery({
    queryKey: ["stock-chart", symbol ?? "none", count, overlays.join(",")],
    queryFn: () => marketApi.chart(symbol as string, { count, overlays }),
    enabled: symbol !== null && symbol !== "",
    refetchInterval: false,
    staleTime: 60_000,
  });
}

/** 랭킹 미리보기. 유니버스 설정을 바꿀 때만 다시 불러요. */
export function useRankingPreview(
  params: {
    type: RankingType;
    duration: RankingDuration;
    market_country?: "KR" | "US";
    count?: number;
  },
  enabled: boolean
) {
  return useQuery({
    queryKey: [
      "ranking-preview",
      params.type,
      params.duration,
      params.market_country,
      params.count,
    ],
    queryFn: () => marketApi.rankings(params),
    enabled,
    staleTime: 300_000,
  });
}

export function usePresets() {
  return useQuery({
    queryKey: ["strategy-presets"],
    queryFn: () => strategiesApi.presets(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useValidateStrategy() {
  return useMutation({
    mutationFn: (dsl: RuleDsl) => strategiesApi.validate(dsl),
  });
}

export function useStrategyLifecycle() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["strategies"] });
  };
  return {
    activate: useMutation({
      // 확인 문구는 여기 고정한다 — 호출부가 문구를 흘리면 서버 계약과 어긋난다.
      mutationFn: (input: { id: string }) =>
        strategiesApi.activate(input.id, { confirm: "실거래" }),
      onSuccess: invalidate,
    }),
    pause: useMutation({
      mutationFn: strategiesApi.pause,
      onSuccess: invalidate,
    }),
    resume: useMutation({
      mutationFn: strategiesApi.resume,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: strategiesApi.remove,
      onSuccess: invalidate,
    }),
    restore: useMutation({
      mutationFn: strategiesApi.restore,
      onSuccess: invalidate,
    }),
    preview: useMutation({ mutationFn: strategiesApi.preview }),
  };
}

// --------------------------------------------------------- orders / positions

/** 토스 `status=OPEN` 그룹과 같은 집합 + 아직 응답을 못 받아 로컬로만 살아 있는 주문. */
const OPEN_STATUSES = new Set(["PENDING", "PENDING_CANCEL", "PENDING_REPLACE", "PARTIAL_FILLED"]);
const IN_FLIGHT_LOCAL_STATES = new Set(["NEW", "SUBMITTING", "SENT_UNKNOWN", "LIVE"]);

function matchesFilter(order: Order, filter: OrderFilter): boolean {
  if (filter === "OPEN") {
    if (order.status !== null) return OPEN_STATUSES.has(order.status);
    return IN_FLIGHT_LOCAL_STATES.has(order.local_state);
  }
  if (filter === "TODAY") {
    const at = order.submitted_at ?? order.filled_at;
    return at !== null && new Date(at).toDateString() === new Date().toDateString();
  }
  return order.status === filter;
}

/**
 * 서버 `/orders` 는 상태·전략 필터가 없다 (계좌·심볼·limit 뿐). 전략 필터는
 * `/strategies/{id}/orders` 로 가고, 상태 필터는 받아온 목록에서 거른다.
 */
/**
 * 환율. 서버가 5분마다 갱신하므로 화면도 그 주기에 맞춰요.
 * 실패하면 `undefined` 로 남고, 호출자는 통화 합산을 포기합니다(추정 환율을 쓰지 않아요).
 */
export function useExchangeRate() {
  return useQuery({
    queryKey: ["exchange-rate"],
    queryFn: () => marketApi.exchangeRate(),
    refetchInterval: 300_000,
    staleTime: 300_000,
  });
}

export function useOrders(params?: {
  status?: OrderFilter;
  strategy_id?: string;
}) {
  const { isAuthenticated } = useAuth();
  const status = params?.status;
  return useQuery({
    queryKey: ["orders", status ?? "all", params?.strategy_id ?? "all"],
    queryFn: () =>
      params?.strategy_id ? strategiesApi.orders(params.strategy_id) : ordersApi.list(),
    select: status
      ? (orders: Order[]) => orders.filter((o) => matchesFilter(o, status))
      : undefined,
    enabled: isAuthenticated,
    refetchInterval: ORDER_POLL_MS,
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: Order) => ordersApi.cancel(order.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["orders"] }),
  });
}

export function usePositions() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["positions"],
    queryFn: positionsApi.list,
    enabled: isAuthenticated,
    refetchInterval: ORDER_POLL_MS,
  });
}

/**
 * 강제 청산 — 포지션 전량을 시장가로 파는 주문을 낸다. 보유일 강제청산(`time_exit.days`)이
 * 하던 일을 사람이 누르는 자리로 옮긴 것이다: 실행 시점이 서버 생존에 달려 있지 않다.
 *
 * `positions` 만 무효화하지 않는다 — 이 호출은 주문을 하나 만들고 장부 현금·재고를
 * 움직이므로, 그 두 화면이 낡은 값을 들고 있으면 방금 낸 매도가 없는 것처럼 보인다.
 * 토스가 거부해도 주문(`REJECTED`)은 남으므로 성공 여부와 무관하게(`onSettled`) 다시 읽는다.
 */
export function useClosePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (positionId: string) => positionsApi.close(positionId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["positions"] });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["book"] });
    },
  });
}

/**
 * 단일 포지션 격리 해제. 같은 종목에 격리된 포지션이 더 있으면 서버가
 * `book-quarantine-multiple-positions` 로 422 를 낸다 — 그 화면은 `useReleaseQuarantineBySymbol`
 * 로 안내해야 한다(단일 경로는 막다른 길이 아니라 다른 경로로의 안내여야 한다).
 */
export function useReleaseQuarantine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (positionId: string) => positionsApi.releaseQuarantine(positionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["positions"] });
      void qc.invalidateQueries({ queryKey: ["book"] });
    },
  });
}

/** 한 종목에 격리된 포지션 전부를 한 번에 해제 — FIFO(`opened_at`)로 실측 총량을 나눠 채운다. */
export function useReleaseQuarantineBySymbol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, symbol }: { accountId: string; symbol: string }) =>
      positionsApi.releaseQuarantineBySymbol(accountId, symbol),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["positions"] });
      void qc.invalidateQueries({ queryKey: ["book"] });
    },
  });
}

export function useConditionalOrders() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["conditional-orders"],
    queryFn: positionsApi.conditionalOrders,
    enabled: isAuthenticated,
    refetchInterval: ACCOUNT_POLL_MS,
  });
}

// ------------------------------------------------------------------ backtests

export function useBacktests(strategyId: string | undefined) {
  return useQuery({
    queryKey: ["backtests", strategyId ?? "all"],
    queryFn: () => backtestsApi.list(strategyId),
    enabled: Boolean(strategyId),
  });
}

export function useBacktest(id: string | undefined) {
  const query = useQuery({
    queryKey: ["backtest", id ?? "none"],
    queryFn: () => backtestsApi.get(id as string),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "PENDING" || status === "RUNNING" ? 3_000 : false;
    },
  });
  return query;
}

/**
 * 백테스트 결과의 종목 차트. 결과는 끝나면 바뀌지 않으므로 다시 받지 않아요 — 봉도 그 백테스트가
 * 쓴 것이라 시간이 지나도 같아요.
 */
export function useBacktestChart(id: string | undefined, symbol: string | null) {
  return useQuery({
    queryKey: ["backtest-chart", id ?? "none", symbol ?? "none"],
    queryFn: () => backtestsApi.chart(id as string, symbol as string),
    enabled: Boolean(id) && symbol !== null && symbol !== "",
    refetchInterval: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateBacktest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BacktestRequest) => backtestsApi.create(body),
    onSuccess: (_data, body) => {
      void qc.invalidateQueries({ queryKey: ["backtests", body.strategy_id] });
    },
  });
}

// -------------------------------------------------------------- notifications

export function useNotificationSettings() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ["notification-settings"],
    queryFn: notificationsApi.settings,
    enabled: isAuthenticated,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettings) => notificationsApi.updateSettings(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notification-settings"] }),
  });
}

// ---------------------------------------------------------------------- admin

export function useAdminOverview() {
  const { isAuthenticated } = useAuth();
  return {
    stats: useQuery({
      queryKey: ["admin", "stats"],
      queryFn: adminApi.stats,
      enabled: isAuthenticated,
    }),
    proxy: useQuery({
      queryKey: ["admin", "proxy"],
      queryFn: adminApi.proxyHealth,
      enabled: isAuthenticated,
      refetchInterval: ACCOUNT_POLL_MS,
    }),
    users: useQuery({
      queryKey: ["admin", "users"],
      queryFn: adminApi.users,
      enabled: isAuthenticated,
    }),
    logs: useQuery({
      queryKey: ["admin", "audit-logs"],
      queryFn: adminApi.auditLogs,
      enabled: isAuthenticated,
    }),
  };
}

export function useAdminKillSwitch() {
  return useMutation({ mutationFn: adminApi.killSwitch });
}
